import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router'
import { cn } from '@/lib/cn'
import { Icon } from './Icon'
import styles from './Button.module.css'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'onDark' | 'accentDim'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface CommonProps {
  readonly variant?: ButtonVariant | undefined
  readonly size?: ButtonSize | undefined
  readonly block?: boolean | undefined
  readonly iconStart?: string | undefined
  readonly iconEnd?: string | undefined
  readonly className?: string | undefined
  readonly children: ReactNode
}

type ButtonProps = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps> & { readonly to?: undefined }
type LinkProps = CommonProps & { readonly to: string }

export function Button(props: ButtonProps | LinkProps) {
  const {
    variant = 'primary',
    size = 'md',
    block = false,
    iconStart,
    iconEnd,
    className,
    children,
  } = props
  const iconSize = size === 'sm' ? 14 : 17
  const classes = cn(styles.button, styles[variant], styles[size], block && styles.block, className)
  const content = (
    <>
      {iconStart && <Icon name={iconStart} size={iconSize} />}
      {children}
      {iconEnd && <Icon name={iconEnd} size={iconSize} />}
    </>
  )

  if ('to' in props && props.to !== undefined) {
    return (
      <Link to={props.to} className={classes}>
        {content}
      </Link>
    )
  }

  const { variant: _v, size: _s, block: _b, iconStart: _is, iconEnd: _ie, className: _c, children: _ch, to: _t, ...rest } =
    props as ButtonProps
  return (
    <button type="button" className={classes} {...rest}>
      {content}
    </button>
  )
}
